import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  Draft,
  EntityState,
} from '@reduxjs/toolkit';
import { IVariable } from 'api/digifi/VariablesApi';
import { variablesApi } from 'store/api';
import createEntitiesSlice from 'handlers/createEntitiesSlice';
import normalizeEntityArray from 'utils/normalizeEntityArray';
import rejectWithValue from 'utils/rejectWithValue';

export enum VariablesActionType {
  LoadVariablesBySystemNames = 'variables/loadVariablesBySystemName',
  SaveVariables = 'variables/saveVariables',
}

const variablesAdapter = createEntityAdapter<IVariable>({});

export interface IVariablesState extends EntityState<IVariable> {
  variablesBySystemName: Record<string, IVariable>,
  unknownEntities: Record<string, boolean>;
  loadingState: Record<string, string>;
}

const initialState: IVariablesState = variablesAdapter.getInitialState({
  variablesBySystemName: {},
  unknownEntities: {},
  loadingState: {},
});

export const saveVariables = createAction<IVariable[]>(VariablesActionType.SaveVariables);

const storeVariablesRecords = (state: Draft<IVariablesState>, variables: IVariable[]) => {
  state.variablesBySystemName = {
    ...state.variablesBySystemName,
    ...normalizeEntityArray(variables, 'systemName'),
  };
};

export const loadVariablesBySystemNames = createAsyncThunk<IVariable[], string[]>(
  VariablesActionType.LoadVariablesBySystemNames,
  async (systemNames: string[], thunkApi) => {
    try {
      const { items } = await variablesApi.search({ systemNames });

      return items;
    } catch (error) {
      return rejectWithValue(error, thunkApi);
    }
  },
);

const { reducer, actions } = createEntitiesSlice<
  IVariablesState
>(
  'variablesSlice',
  initialState,
  (builder) => {
    builder.addCase(loadVariablesBySystemNames.pending, (state, action) => {
      action.meta.arg.forEach((systemName) => {
        if (!state.loadingState[systemName]) {
          state.loadingState[systemName] = action.meta.requestId;
        }
      });
    });

    builder.addCase(saveVariables, (state, action) => {
      storeVariablesRecords(state, action.payload);
    });

    builder.addCase(loadVariablesBySystemNames.fulfilled, (state, action) => {
      action.meta.arg.forEach((systemName) => {
        if (state.loadingState[systemName] === action.meta.requestId) {
          delete state.loadingState[systemName];
        }
      });

      variablesAdapter.addMany(state, action.payload);

      action.payload.forEach((variable) => {
        state.variablesBySystemName[variable.systemName] = variable;
      });
    });

    builder.addCase(loadVariablesBySystemNames.rejected, (state, action) => {
      action.meta.arg.forEach((systemName) => {
        if (state.loadingState[systemName] === action.meta.requestId) {
          delete state.loadingState[systemName];
        }
      });
    });
  }, {
  shouldMarkEntityAsUnknown: (field, state) => {
    return !state.variablesBySystemName[field];
  },
});

export const {
  recomputeUnknownEntitiesFor: recomputeUnknownVariablesFor,
} = actions;

export default reducer;
